import React, { use, useCallback, useMemo } from "react";
import MissionDetails from "../../components/editors/MissionDetails";
import { useQuery } from "@apollo/client/react";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { useI18n } from "../../hooks/i18n-hooks";
import {
    AmendmentSignatureStatus,
    AmendmentState,
    ContractSignatureStatus,
    ContractState,
    DocumentType,
    EarningType,
    ProfileType,
} from "../../__generated__/graphql";
import { GET_MISSION_WITH_TERMINATION } from "../../graphql/core";
import {
    useMissionDisplayAttributes,
    useMissionHolidayBalance,
    useMissionOvertimeBalance,
} from "../../hooks/missions";
import Loading from "../../components/Loading";
import ScreenHeader, { MenuAction } from "./ScreenHeader";
import { formatDate, openPhoneNumber } from "../../utils";
import { useAddMissionToCalendar } from "../../hooks/calendar";
import ScreenCard, { ButtonType, ScreenCardHandle } from "../ScreenCard";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import { UserMissionsTabParamList } from "../../navigations/user/UserMissionsNavigation";
import { useSetAlert } from "../../hooks/use-set-alert";
import { CreateMissionMode } from "../../contexts/CreateMissionManager";
import { GET_MISSION_AMENDMENTS } from "../../graphql/amendment";
import { useTheme } from "../../hooks/use-theme";

type Props = StackScreenProps<RootStackParamList & UserMissionsTabParamList, "MissionDetails">;

const MissionDetailsScreen: React.FC<Props> = ({ route, navigation }) => {
    const i18n = useI18n();
    const { colors } = useTheme();
    const { profileType, profileId } = use(ProfileSwitcherManagerContext);
    const { loading, error, data, refetch } = useQuery(GET_MISSION_WITH_TERMINATION, {
        variables: { id: route?.params?.missionId || "" },
        fetchPolicy: "network-only",
    });
    const { data: { getMissionAmendments = [] } = {}, refetch: amendmentsRefetch } = useQuery(
        GET_MISSION_AMENDMENTS,
        {
            fetchPolicy: "cache-and-network",
            variables: {
                missionId: route.params.missionId,
                includeRejected: true,
            },
        },
    );

    const addMissionToCalendar = useAddMissionToCalendar();

    const mission = data?.getMission;

    const [readConfirmed, setReadConfirmed] = React.useState(false);
    const [readConfirmedError, setReadConfirmedError] = React.useState(false);

    const [otherName, otherPhone] = useMemo(() => {
        if (mission) {
            if (profileType === ProfileType.Client) {
                return [mission.workerInfo.name, mission.workerInfo.phoneNumber];
            }
            if (profileType === ProfileType.Worker) {
                return [mission.clientInfo.name, mission.clientInfo.phoneNumber];
            }
        }
        return ["", ""];
    }, [mission, profileType]);

    const { setAlert } = useSetAlert();

    const onDuplicateMission = useCallback(() => {
        if (mission) {
            navigation?.navigate("CreateMissionStack", {
                createMissionMode: CreateMissionMode.New,
                initialMission: mission,
            });
        }
    }, [mission, navigation]);

    const getMissionDisplayAttributes = useMissionDisplayAttributes();

    const notAppliedYetAmendment = useMemo(
        () => getMissionAmendments.find((amendment) => amendment.state === AmendmentState.Accepted),
        [getMissionAmendments],
    );

    const {
        canCancelOrTerminate,
        canEdit,
        canUpdate,
        canDuplicate,
        isMissionAwaitingWorkerSignature,
        hasExistingPendingAvenant,
    } = useMemo(
        () => getMissionDisplayAttributes(mission, profileType, data?.getTerminationInfo),
        [data?.getTerminationInfo, getMissionDisplayAttributes, mission, profileType],
    );

    const onEditMission = useCallback(() => {
        if (mission) {
            if (hasExistingPendingAvenant) {
                setAlert({
                    title: i18n.t("mission.existing_avenant.title"),
                    message: i18n.t("mission.existing_avenant.description"),
                });
            } else if (isMissionAwaitingWorkerSignature) {
                setAlert({
                    title: i18n.t("mission.edit_blocked"),
                    message: i18n.t("mission.edit_blocked_description"),
                });
            } else if (notAppliedYetAmendment) {
                setAlert({
                    title: i18n.t("mission.amendment.awaiting_application_title"),
                    message: i18n.t("mission.amendment.awaiting_application_description", {
                        applyDate: formatDate(new Date(notAppliedYetAmendment.applyDate)),
                    }),
                });
            } else {
                navigation?.navigate("MissionEdit", {
                    missionId: mission.id,
                });
            }
        }
    }, [
        hasExistingPendingAvenant,
        i18n,
        isMissionAwaitingWorkerSignature,
        mission,
        navigation,
        notAppliedYetAmendment,
        setAlert,
    ]);

    const onUpdateMission = useCallback(() => {
        // BAS-2916: no start date check here — canUpdate already gates this action (contract still
        // cancellable and not signed by anyone), which covers a retroactive contract nobody signed
        // yet. Re-checking the start date turned the button into a no-op for those contracts.
        if (mission?.id) {
            navigation?.navigate("CreateMissionStack", {
                createMissionMode: CreateMissionMode.Update,
                initialMission: mission,
            });
        }
    }, [mission, navigation]);

    const actions = useMemo(() => {
        if (!mission || loading) {
            // do not display menu if we don't have a mission or a new one is loading
            return [];
        }
        const result: MenuAction[] = [
            {
                label: i18n.t("mission.contact_other", { name: otherName }),
                icon: "phone-call",
                onPress: () => {
                    if (otherPhone) void openPhoneNumber(otherPhone);
                },
            },
            ...(mission?.currentContract?.state &&
            ![ContractState.Cancelled, ContractState.Terminated, ContractState.Ended].includes(
                mission?.currentContract?.state,
            )
                ? [
                      {
                          label: i18n.t("mission.add_to_calendar"),
                          icon: "calendar" as const,
                          onPress: () => {
                              if (mission) void addMissionToCalendar(mission);
                          },
                      },
                  ]
                : []),
        ];

        if (canUpdate) {
            result.push({
                label: i18n.t("mission.mission_edit"),
                icon: "write",
                onPress: onUpdateMission,
            });
        }

        if (canEdit) {
            result.push({
                label: i18n.t("mission.mission_edit"),
                icon: "write",
                color:
                    isMissionAwaitingWorkerSignature ||
                    hasExistingPendingAvenant ||
                    notAppliedYetAmendment
                        ? colors.fieldLabel
                        : undefined,
                onPress: onEditMission,
            });
        }

        if (canDuplicate) {
            result.push({
                label: i18n.t("mission.mission_duplicate"),
                icon: "clone",
                onPress: onDuplicateMission,
            });
        }
        if (canCancelOrTerminate) {
            result.push({
                label: i18n.t("mission.cancel_mission"),
                icon: "cancel-mission",
                color: colors.error,
                onPress: () => {
                    if (mission) {
                        navigation?.navigate("MissionTermination", {
                            missionId: mission.id,
                        });
                    }
                },
            });
        }
        return result;
    }, [
        mission,
        loading,
        i18n,
        otherName,
        canUpdate,
        canEdit,
        canDuplicate,
        canCancelOrTerminate,
        otherPhone,
        addMissionToCalendar,
        onUpdateMission,
        isMissionAwaitingWorkerSignature,
        hasExistingPendingAvenant,
        notAppliedYetAmendment,
        colors.fieldLabel,
        colors.error,
        onEditMission,
        onDuplicateMission,
        navigation,
    ]);

    const isPendingSignatureByCurrentUser = useMemo(() => {
        return (
            // we may also enable the button if the status is in "ContractSignatureStatus.WaitingSelfSign"
            // ContractSignatureStatus.WaitingSelfSign means that the contract has already a signature request
            // that has not been signed by the current user
            [
                ContractSignatureStatus.NeedSelfSign,
                ContractSignatureStatus.WaitingSelfSign,
            ].includes(
                mission?.currentContract?.signatureStatus ||
                    ContractSignatureStatus.WaitingContract,
            ) &&
            (mission?.signerInfo?.id === profileId || profileType === ProfileType.Worker)
        );
    }, [
        mission?.currentContract?.signatureStatus,
        mission?.signerInfo?.id,
        profileId,
        profileType,
    ]);

    const { refetch: holidayBalanceRefetch, balance: holidayBalance } = useMissionHolidayBalance(
        mission?.currentContract?.earningType !== EarningType.Package ? mission?.id : undefined,
    );
    // overtime is only available for monthly missions
    const { refetch: overtimeBalanceRefetch, balance: overtimeBalance } = useMissionOvertimeBalance(
        mission?.currentContract?.earningType === EarningType.Monthly ? mission?.id : undefined,
    );

    const fullRefetch = useCallback(async () => {
        await Promise.all([
            refetch(),
            holidayBalanceRefetch(),
            overtimeBalanceRefetch(),
            amendmentsRefetch(),
        ]);
    }, [holidayBalanceRefetch, refetch, overtimeBalanceRefetch, amendmentsRefetch]);

    const hasAnnexes = useMemo(() => {
        return mission?.documents.some((doc) => doc.type === DocumentType.MissionAnnex);
    }, [mission?.documents]);

    const canSign = useMemo(() => {
        return profileType === ProfileType.Client || !hasAnnexes || readConfirmed;
    }, [profileType, hasAnnexes, readConfirmed]);

    const ref = React.useRef<ScreenCardHandle>(null);

    const needSelfSignAmendment = useMemo(
        () =>
            getMissionAmendments.find(
                (amendment) =>
                    amendment.state === AmendmentState.PendingAcceptation &&
                    amendment.signatureStatus === AmendmentSignatureStatus.NeedSelfSign,
            ),
        [getMissionAmendments],
    );

    const buttons = useMemo<ButtonType[]>(() => {
        return mission
            ? isPendingSignatureByCurrentUser
                ? [
                      {
                          title: i18n.t("screens.user.missions.read_and_sign_contract"),
                          onPress: () => {
                              if (canSign) {
                                  navigation?.navigate("ContractPreviewScreen", {
                                      contractId: mission.currentContract?.id || "",
                                  });
                              } else {
                                  setReadConfirmedError(true);
                                  ref.current?.scrollContentToEnd();
                              }
                          },
                          mode: canSign ? "secondary" : "disabled",
                      },
                  ]
                : needSelfSignAmendment
                  ? [
                        {
                            title: i18n.t("mission.details.amendment.verify_and_sign"),
                            onPress: () => {
                                navigation?.navigate("ContractPreviewScreen", {
                                    contractId: needSelfSignAmendment.id,
                                    contractType: "amendment",
                                });
                            },
                            mode: "secondary",
                        },
                    ]
                  : []
            : [];
    }, [
        mission,
        isPendingSignatureByCurrentUser,
        i18n,
        canSign,
        needSelfSignAmendment,
        navigation,
    ]);

    const _setReadConfirmed = useCallback((newValue: boolean) => {
        if (newValue) {
            setReadConfirmedError(false);
        }
        setReadConfirmed(newValue);
    }, []);

    return (
        <>
            <ScreenHeader
                useSmallLabel
                onLeftAction={() => navigation.goBack()}
                label={i18n.t("mission.nav_title")}
                actions={actions}
            ></ScreenHeader>
            <ScreenCard
                shrinkButtonToIcon="signature"
                slideButtonUp
                refresh={fullRefetch}
                buttons={buttons}
                automaticallyAdjustKeyboardInsets={false}
                headband={
                    error
                        ? {
                              text: i18n.t("screens.user.missions.cannot_load_missions"),
                              mode: "error",
                          }
                        : undefined
                }
                ref={ref}
            >
                {!mission && loading && <Loading />}
                {mission && profileType && (
                    <MissionDetails
                        onReadConfirmed={_setReadConfirmed}
                        onEditMission={onEditMission}
                        onUpdateMission={onUpdateMission}
                        onDuplicateMission={onDuplicateMission}
                        readConfirmedError={readConfirmedError}
                        mission={mission}
                        terminationInfo={data.getTerminationInfo}
                        navigation={navigation}
                        profileType={profileType}
                        holidayBalance={holidayBalance}
                        overtimeBalance={overtimeBalance}
                        missionAmendments={getMissionAmendments}
                    />
                )}
            </ScreenCard>
        </>
    );
};

export default MissionDetailsScreen;
